/**
 * The ServiceM8 sync as the worker, the API and the Settings screen all see
 * it. The worker lives in the migrator and the API never imports it, so what
 * the three have to agree on — how far back the window reaches, when a run
 * has stopped answering, the shape of its progress — is declared here once.
 */

/**
 * How far back the window reaches, in days. The team compares the last few
 * days as well as what is ahead, so a sync puts three days of history back in
 * line with ServiceM8 along with everything booked from today on.
 */
export const SYNC_DAYS_BACK = 3

/**
 * How often a running sync touches its row to say it is still alive.
 */
export const SYNC_HEARTBEAT_MS = 30_000

/**
 * A RUNNING sync whose row has not been touched for this long is dead: the
 * process was killed, or the machine restarted under it. Long enough to
 * survive a slow statement between heartbeats, short enough that a restart
 * does not block the button for an afternoon.
 */
export const SYNC_STALE_AFTER_MS = 10 * 60_000

export type SyncStage = 'fetch' | 'rebuild' | 'files'

export const SYNC_STAGES: ReadonlyArray<{ stage: SyncStage; label: string }> = [
  { stage: 'fetch', label: 'Fetching from ServiceM8' },
  { stage: 'rebuild', label: 'Rebuilding jobs' },
  { stage: 'files', label: 'Fetching files' },
]

/** Written to `MigrationRun.stats.progress` while a sync runs. */
export interface SyncProgress {
  stage: SyncStage
  /** What is happening inside the stage, in words: `job`, `before.jpg`. */
  label: string
  /** Units done within the stage, and how many there are when that is known. */
  done: number
  total: number | null
  /** A second counter for the item named by `label`, such as records fetched. */
  count?: number
}

/** The summary a finished forward sync leaves in `MigrationRun.stats`. */
export interface SyncSummary {
  kind: 'forward'
  windowFrom: string
  jobs: number
  booked: number
  open: number
  /** Records ServiceM8 sent, and whether the fetch was the first full one. */
  fetched: number
  fullFetch: boolean
  /** Rows the loaders could not write. Zero on a clean run. */
  loadFailures: number
  files: {
    transferred: number
    skipped: number
    failed: number
    remaining: number
    stopReason: string | null
  } | null
  requests: number
}

/** Whether a RUNNING row's last heartbeat is recent enough to believe. */
export function syncAlive(updatedAt: Date, now: number = Date.now()): boolean {
  return now - updatedAt.getTime() < SYNC_STALE_AFTER_MS
}
